import { PrimaryButton } from '../../../components/buttons/PrimaryButton';
import { SecondaryButton } from '../../../components/buttons/SecondaryButton';
import FloatingCard from '../../../components/general/FloatingCard';
import { NoticeRow } from '../../../components/general/NoticeRow';
import ConfirmTakeoffDialog from '../../../components/dialogs/ConfirmTakeoffDialog';
import ConfirmCancelMissionDialog from '../../../components/dialogs/ConfirmCancelMissionDialog';
import MissionLogsDialog from '../../../components/dialogs/MissionLogsDialog';
import SubmitDiscrepancyReportDialog from '../../../components/dialogs/SubmitDiscrepancyReportDialog';
import VehicleModuleVersionsDialog from '../../../components/dialogs/VehicleModuleVersionsDialog';
import { InfoRow } from '../../../components/general/InfoRow';
import { Text } from '../../../components/general/Text';
import { en } from '../../../locales/en';
import { useDialogQueueStore } from '../../../store/useDialogQueueStore';
import { useGcsStore } from '../../../store/useGcsStore';
import type { VehicleId } from '../../../types/gcs/vehicle';
import type { MenuItemTileProps } from '../../../components/general/MenuItemTile';
import { formatDistanceMtoKm, formatPercentage, formatWeightKg } from '../../../utils/formatters';
import VehiclePanelHeader from './VehiclePanelHeader';

const noticesCardStyle: React.CSSProperties = {
  border: 'var(--card-border-stroke) var(--card-border-style) #FFF1C2',
  borderRadius: 'var(--card-border-radius)',
  padding: '8px',
  backgroundColor: '#FFFBEB'
};

interface PreflightVehiclePanelProps {
  vehicleId: VehicleId;
  gcsId: string;
  onClose: () => void;
  onFocusVehicle: () => void;
}

function PreflightVehiclePanel({ vehicleId, gcsId, onClose, onFocusVehicle }: PreflightVehiclePanelProps) {
  const { showDialog, hideDialog } = useDialogQueueStore();

  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId);
  });

  const preflightOverview = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData || !liveMission) return undefined;
    return gcsData.missionPreflightOverviewMap.get(liveMission.missionId);
  });

  const takeoffProhibition = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData || !liveMission) return undefined;
    const preflightState = gcsData.missionPreflightStateMap.get(liveMission.missionId);
    return preflightState?.takeoffProhibition;
  });

  const onCancelMissionClicked = () => {
    if (!liveMission) return;
    showDialog({
      id: `confirm-cancel-mission-${liveMission.missionId}`,
      component: (
        <ConfirmCancelMissionDialog
          gcsId={gcsId}
          vehicleId={vehicleId}
          missionId={liveMission.missionId}
          onClose={hideDialog}
        />
      )
    });
  };

  const onTakeoffClicked = () => {
    if (!liveMission) return;
    showDialog({
      id: `approve-takeoff-${vehicleId}`,
      component: (
        <ConfirmTakeoffDialog
          gcsId={gcsId}
          vehicleId={vehicleId}
          missionId={liveMission.missionId}
          onClose={hideDialog}
        />
      )
    });
  };

  const menuItems: MenuItemTileProps[] = [
    {
      label: en.vehiclePanelMenuItems.showVersions,
      onClick: () => {
        showDialog({
          id: `vehicle-module-versions-${vehicleId}`,
          component: <VehicleModuleVersionsDialog vehicleId={vehicleId} onClose={hideDialog} />
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.missionLogs,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: `mission-logs-dialog-${liveMission.missionId}`,
          component: (
            <MissionLogsDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={hideDialog}
            />
          )
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.discrepancyReport,
      onClick: () => {
        showDialog({
          id: 'submit-discrepancy-report',
          component: (
            <SubmitDiscrepancyReportDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              onClose={() => useDialogQueueStore.getState().hideDialog()}
            />
          )
        });
      }
    }
  ];

  return (
    <FloatingCard>
      <VehiclePanelHeader
        vehicleId={vehicleId}
        gcsId={gcsId}
        onFocusVehicle={onFocusVehicle}
        onClose={onClose}
        menuItems={menuItems}
      />

      {/* Scrollable Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '8px',
          flex: 1,
          overflowY: 'auto'
        }}
      >
        {/* Notices Card */}
        {liveMission &&
          (liveMission.package?.order?.isFirstToDeliveryPoint ||
            liveMission.package?.order?.isPriority ||
            liveMission.package?.isRefly) && (
            <div style={noticesCardStyle}>
              {liveMission.package?.order?.isFirstToDeliveryPoint && (
                <NoticeRow text={en.preflightPanel.firstToDeliveryPoint} />
              )}
              {liveMission.package?.order?.isPriority && <NoticeRow text={en.preflightPanel.jetDelivery} />}
              {liveMission.package?.isRefly && <NoticeRow text={en.preflightPanel.refly} />}
            </div>
          )}

        {/* Mission Info Card */}
        {liveMission && (
          <div
            style={{
              border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
              borderRadius: 'var(--card-border-radius)',
              padding: '16px',
              backgroundColor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            <InfoRow label={en.preflightPanel.order} value={liveMission.package?.order?.name ?? '-'} />
            <InfoRow
              label={en.preflightPanel.deliveryPoint}
              value={liveMission.package?.order?.deliveryPoint?.name ?? '-'}
            />
            <InfoRow
              label={en.preflightPanel.pickupPoint}
              value={liveMission.pickupPoint?.name ?? en.preflightPanel.launchpad}
            />
            <InfoRow
              label={en.preflightPanel.distanceRoundtrip}
              value={
                preflightOverview
                  ? formatDistanceMtoKm(preflightOverview.batteryConsumptionEstimation.flightDistanceM)
                  : '-'
              }
            />
            <InfoRow
              label={en.preflightPanel.requiredBattery}
              value={
                preflightOverview
                  ? formatPercentage(preflightOverview.batteryConsumptionEstimation.totalConsumptionPct)
                  : '-'
              }
            />
            <InfoRow
              label={en.preflightPanel.packageWeight}
              value={liveMission.package ? formatWeightKg(liveMission.package.weightKg) : '-'}
            />
          </div>
        )}
      </div>

      {/* Button Container */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '8px',
          gap: '16px'
        }}
      >
        {/* Takeoff Prohibition Warning */}
        {takeoffProhibition && (
          <Text
            as='span'
            variant='body2'
            style={{ color: 'var(--color-error)', display: 'block', textAlign: 'center' }}
          >
            {takeoffProhibition}
          </Text>
        )}

        {/* Buttons Row */}
        <div
          style={{
            display: 'flex',
            gap: '12px'
          }}
        >
          {/* Cancel Mission Button */}
          <div style={{ flex: 1 }}>
            <SecondaryButton text={en.preflightPanel.cancelMissionButton} callback={onCancelMissionClicked} />
          </div>

          {/* Takeoff Button */}
          <div style={{ flex: 1 }}>
            <PrimaryButton
              text={en.preflightPanel.takeoffButton}
              callback={onTakeoffClicked}
              backgroundColor={'var(--color-success)'}
              foregroundColor={'#ffffff'}
              isEnabled={!takeoffProhibition}
            />
          </div>
        </div>
      </div>
    </FloatingCard>
  );
}

export default PreflightVehiclePanel;
